import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  Legend,
} from 'recharts'
import {
  Tag,
  Banknote,
  Award,
  QrCode,
  UserPlus,
  Send,
  ChevronRight,
  AlertTriangle,
  Sparkles,
  Trophy,
  Cake,
  Activity,
  CheckCircle2,
  Circle,
  Printer,
  CalendarClock,
  Info,
  LifeBuoy,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import apiClient from '@/api/client'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useReportRange } from '@/hooks/useReportRange'
import { DateRangeFilter } from '@/components/admin/reports/DateRangeFilter'
import { MetricTile } from '@/components/admin/reports/MetricTile'
import { metricPairOrEmpty } from '@/components/admin/dashboard/dashboardFormat'
import { cn, formatDateHuman, formatDateRangeHuman } from '@/lib/utils'
import type {
  APIResponse,
  MarketingOverviewResponse,
  MarketingActivityEvent,
  MarketingTopPromo,
  MarketingTopQR,
} from '@/types'
import { MarketingShell } from './MarketingShell'
import { chartTooltip } from '@/lib/chartTheme'

// ── helpers ─────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  return n.toLocaleString('en-US')
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'just now'
  const m = Math.floor(ms / 60_000)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d ago`
  return formatDateHuman(iso)
}

function activityIcon(kind: MarketingActivityEvent['kind']) {
  switch (kind) {
    case 'punch':
      return <Award className="h-3.5 w-3.5" />
    case 'reward':
      return <Sparkles className="h-3.5 w-3.5" />
    case 'promo_redemption':
      return <Tag className="h-3.5 w-3.5" />
    case 'qr_scan':
      return <QrCode className="h-3.5 w-3.5" />
    default:
      return <Activity className="h-3.5 w-3.5" />
  }
}

function activityTone(kind: MarketingActivityEvent['kind']): string {
  switch (kind) {
    case 'punch':
      return 'text-amber-600 dark:text-amber-400'
    case 'reward':
      return 'text-purple-600 dark:text-purple-400'
    case 'promo_redemption':
      return 'text-emerald-600 dark:text-emerald-400'
    case 'qr_scan':
      return 'text-sky-600 dark:text-sky-400'
    default:
      return 'text-muted-foreground'
  }
}

// Lifecycle stage chip palette. Matches the Customers tab so a returning
// owner reads the same colour for "lapsed" or "at-risk" everywhere.
const LIFECYCLE_COLORS: Record<string, string> = {
  new: '#22c55e',
  active: '#3b82f6',
  at_risk: '#f59e0b',
  lapsed: '#ef4444',
  vip: '#a855f7',
  unknown: '#9ca3af',
}

const LIFECYCLE_LABEL: Record<string, string> = {
  new: 'New',
  active: 'Active',
  at_risk: 'At-risk',
  lapsed: 'Lapsed',
  vip: 'VIP',
  unknown: 'Unknown',
}

// ── page ────────────────────────────────────────────────────────────

export function AdminMarketingOverview() {
  const range = useReportRange()
  const { formatCurrency } = useCurrency()
  const navigate = useNavigate()

  const query = useQuery<APIResponse<MarketingOverviewResponse>>({
    queryKey: ['marketing-overview', range.fromISO, range.toISO],
    queryFn: () => apiClient.getMarketingOverview(range.fromISO, range.toISO),
    staleTime: 30_000,
  })

  const data = query.data?.data
  const isLoading = query.isLoading || !data

  const comparisonLabel = useMemo(() => {
    switch (range.preset) {
      case 'today':
        return 'vs. yesterday'
      case 'yesterday':
        return 'vs. day before'
      case 'last_7':
        return 'vs. previous 7 days'
      case 'last_30':
        return 'vs. previous 30 days'
      case 'this_month':
        return 'vs. last month'
      case 'last_month':
        return 'vs. month before'
      default:
        return 'vs. previous period'
    }
  }, [range.preset])

  return (
    <MarketingShell
      title="Performance"
      description="The detailed numbers behind your marketing — offers, rewards, scans and guests"
      actions={<DateRangeFilter range={range} />}
    >
      <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Showing data for{' '}
        <span className="font-medium text-foreground">
          {formatDateRangeHuman(range.from, range.to)}
        </span>
        . KPIs and engagement below cover this window — customer mix, birthdays and live activity are live snapshots.
      </p>

      {query.isError && (
        <Card className="border-destructive/50">
          <CardContent className="p-4 text-sm text-destructive">
            Failed to load marketing overview:{' '}
            {String((query.error as Error)?.message ?? 'unknown')}
          </CardContent>
        </Card>
      )}


      {/* "What now" — Top performers · Needs attention · Quick actions.
          Moved above the KPIs so the first thing an owner sees is what to do
          next, not six metrics that often read zero on a fresh account. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <TopPerformersCard
          isLoading={isLoading}
          bestPromo={data?.top_performers.best_promo}
          topQR={data?.top_performers.top_qr}
          formatCurrency={formatCurrency}
        />
        <NeedsAttentionCard
          isLoading={isLoading}
          data={data}
          onSendToLapsed={() =>
            navigate({ to: '/admin/marketing/segments', search: { segment: 'lapsed' } as never })
          }
        />
        <QuickActionsCard />
      </div>

      {/* SECTION 1 — "The Pulse". 6 KPI tiles with sparklines. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5 space-y-3">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-7 w-32" />
                <Skeleton className="h-4 w-40" />
              </CardContent>
            </Card>
          ))
        ) : (
          <>
            <MetricTile
              label="Promo redemptions"
              metric={data.kpis.promo_redemptions}
              formatValue={formatNumber}
              comparisonLabel={comparisonLabel}
              icon={<Tag className="h-4 w-4" />}
              sparkline={data.kpis.promo_redemptions.sparkline}
              tooltip={{
                summary:
                  'How many times a promo code was used at checkout in this window. Cancelled orders are excluded.',
              }}
            />
            <MetricTile
              label="Promo revenue"
              metric={metricPairOrEmpty(data.kpis.promo_revenue_pkr)}
              formatValue={formatCurrency}
              comparisonLabel={comparisonLabel}
              icon={<Banknote className="h-4 w-4" />}
              sparkline={data.kpis.promo_revenue_pkr.sparkline}
              tooltip={{
                summary:
                  'Tax-inclusive sales on orders that used a promo code in this window — the closed-loop "did this campaign earn anything" number.',
              }}
            />
            <MetricTile
              label="Loyalty rewards"
              metric={data.kpis.loyalty_rewards}
              formatValue={formatNumber}
              comparisonLabel={comparisonLabel}
              icon={<Award className="h-4 w-4" />}
              sparkline={data.kpis.loyalty_rewards.sparkline}
              tooltip={{
                summary:
                  'Loyalty rewards customers redeemed at checkout in this window (a punch card filled and cashed in).',
              }}
            />
            <MetricTile
              label="QR scans"
              metric={data.kpis.qr_scans}
              formatValue={formatNumber}
              comparisonLabel={comparisonLabel}
              icon={<QrCode className="h-4 w-4" />}
              sparkline={data.kpis.qr_scans.sparkline}
              tooltip={{
                summary:
                  'Real human scans of your marketing QR codes in this window. Bots are filtered out.',
              }}
            />
            <MetricTile
              label="New customers"
              metric={data.kpis.new_customers}
              formatValue={formatNumber}
              comparisonLabel={comparisonLabel}
              icon={<UserPlus className="h-4 w-4" />}
              sparkline={data.kpis.new_customers.sparkline}
              tooltip={{
                summary:
                  'Customers whose first visit to your restaurant happened in this window — the growth signal.',
              }}
            />
            <ReachableCustomersTile data={data.kpis.reachable_customers} />
          </>
        )}
      </div>

      {/* SECTION 2 — Customer mix slim stacked bar. */}
      {!isLoading && <CustomerMixBar mix={data.customer_mix} />}

      {/* SECTION 3 — Engagement over time. */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Engagement over time</CardTitle>
          <p className="text-sm text-muted-foreground">
            Promo redemptions, loyalty events and QR scans per day.
          </p>
        </CardHeader>
        <CardContent className="h-[280px]">
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : data.engagement_daily.every(
              d => d.promo === 0 && d.loyalty === 0 && d.qr === 0,
            ) ? (
            <EmptyState message="No marketing activity in this window — make some noise →" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={data.engagement_daily}
                margin={{ top: 12, right: 12, bottom: 0, left: 0 }}
              >
                <defs>
                  <linearGradient id="gradPromo" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="gradLoyalty" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a855f7" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="gradQR" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  tickFormatter={(iso: string) => formatDateHuman(iso)}
                />
                <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} allowDecimals={false} width={36} />
                <ReTooltip
                  {...chartTooltip}
                  labelFormatter={(iso: string) => formatDateHuman(iso)}
                />
                <Legend
                  verticalAlign="top"
                  height={28}
                  iconSize={8}
                  formatter={(value: string) => <span className="text-sm">{value}</span>}
                />
                <Area
                  type="monotone"
                  name="Promo"
                  dataKey="promo"
                  stackId="1"
                  stroke="#10b981"
                  fill="url(#gradPromo)"
                  strokeWidth={1.5}
                />
                <Area
                  type="monotone"
                  name="Loyalty program"
                  dataKey="loyalty"
                  stackId="1"
                  stroke="#a855f7"
                  fill="url(#gradLoyalty)"
                  strokeWidth={1.5}
                />
                <Area
                  type="monotone"
                  name="QR"
                  dataKey="qr"
                  stackId="1"
                  stroke="#0ea5e9"
                  fill="url(#gradQR)"
                  strokeWidth={1.5}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* SECTION 5 — Occasions this week + Live activity. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <OccasionsCard isLoading={isLoading} occasions={data?.occasions_this_week ?? []} />
        <LiveActivityCard isLoading={isLoading} events={data?.recent_activity ?? []} />
      </div>

      {/* SECTION 5b — Guest recovery. Its own query: this is the only money
          figure on the page that is business-day attributed rather than
          calendar-ranged, so it deliberately does not ride the overview
          aggregator's payload. */}
      <GuestRecoveryCard fromISO={range.fromISO} toISO={range.toISO} />

      {/* SECTION 6 — Channel status strip. */}
      {!isLoading && <ChannelStatusStrip data={data} />}
      </div>
    </MarketingShell>
  )
}

// ── sub-components ──────────────────────────────────────────────────

function EmptyState({ message }: { message: string }) {
  return (
    <div className="h-full flex items-center justify-center text-sm text-muted-foreground text-center px-4">
      {message}
    </div>
  )
}

function ReachableCustomersTile({
  data,
}: {
  data: { with_phone: number; with_email: number; total: number }
}) {
  return (
    <Card>
      <CardContent className="p-5 space-y-2">
        <div className="flex items-center justify-between">
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1 cursor-help underline-offset-2 decoration-dotted hover:underline text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  <span>Reachable customers</span>
                  <Info className="h-3 w-3 text-muted-foreground/70" aria-hidden />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-left">
                <div className="space-y-1">
                  <div className="text-sm font-semibold leading-tight text-popover-foreground">
                    Reachable customers
                  </div>
                  <p className="text-sm leading-relaxed text-popover-foreground/90">
                    Customers in your directory with a phone or email on file — the audience you can actually send a campaign to. Snapshot, not windowed.
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span className="text-muted-foreground">
            <Send className="h-4 w-4" />
          </span>
        </div>
        <p className="text-2xl font-semibold tabular-nums">
          {formatNumber(Math.max(data.with_phone, data.with_email))}
        </p>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>{formatNumber(data.with_phone)} phone</span>
          <span className="text-muted-foreground/40">·</span>
          <span>{formatNumber(data.with_email)} email</span>
          <span className="text-muted-foreground/40">·</span>
          <span>{formatNumber(data.total)} total</span>
        </div>
      </CardContent>
    </Card>
  )
}

function CustomerMixBar({ mix }: { mix: MarketingOverviewResponse['customer_mix'] }) {
  const segments = [
    { key: 'new', value: mix.new },
    { key: 'active', value: mix.active },
    { key: 'at_risk', value: mix.at_risk },
    { key: 'lapsed', value: mix.lapsed },
    { key: 'vip', value: mix.vip },
    { key: 'unknown', value: mix.unknown },
  ].filter(s => s.value > 0)
  const total = segments.reduce((a, b) => a + b.value, 0)
  if (total === 0) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          No customers in the directory yet —{' '}
          <Link to="/admin/customers" className="text-primary underline-offset-4 hover:underline">
            add or capture some at checkout
          </Link>
          .
        </CardContent>
      </Card>
    )
  }
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium">Customer mix</p>
          <p className="text-sm text-muted-foreground">
            {formatNumber(total)} customer{total === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
          {segments.map(s => (
            <div
              key={s.key}
              className="h-full"
              style={{
                width: `${(s.value / total) * 100}%`,
                backgroundColor: LIFECYCLE_COLORS[s.key] ?? LIFECYCLE_COLORS.unknown,
              }}
              title={`${LIFECYCLE_LABEL[s.key] ?? s.key}: ${formatNumber(s.value)}`}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
          {segments.map(s => (
            <Link
              key={s.key}
              to="/admin/customers"
              search={{ stage: s.key } as never}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: LIFECYCLE_COLORS[s.key] ?? LIFECYCLE_COLORS.unknown }}
              />
              <span className="font-medium text-foreground">{formatNumber(s.value)}</span>{' '}
              {LIFECYCLE_LABEL[s.key] ?? s.key}
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function TopPerformersCard({
  isLoading,
  bestPromo,
  topQR,
  formatCurrency,
}: {
  isLoading: boolean
  bestPromo?: MarketingTopPromo
  topQR?: MarketingTopQR
  formatCurrency: (n: number) => string
}) {
  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Trophy className="h-4 w-4 text-amber-500" />
          Top performers
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <>
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </>
        ) : !bestPromo && !topQR ? (
          <p className="text-sm text-muted-foreground">
            Nothing has driven engagement yet —{' '}
            <Link to="/admin/marketing/promo-codes" className="text-primary underline-offset-4 hover:underline">
              run your first promo
            </Link>
            .
          </p>
        ) : (
          <>
            {bestPromo && (
              <Link
                to="/admin/marketing/promo-codes"
                className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 p-3 hover:bg-muted/40 transition-colors"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Tag className="h-3.5 w-3.5 text-emerald-600" />
                    <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                      Best promo
                    </p>
                  </div>
                  <p className="mt-1 truncate text-sm font-semibold">{bestPromo.code}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatNumber(bestPromo.redemptions)} redemption
                    {bestPromo.redemptions === 1 ? '' : 's'} ·{' '}
                    {formatCurrency(bestPromo.revenue_pkr)} attributed
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
            )}
            {topQR && (
              <Link
                to="/admin/marketing/qr-codes"
                className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 p-3 hover:bg-muted/40 transition-colors"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <QrCode className="h-3.5 w-3.5 text-sky-600" />
                    <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                      Most-scanned QR
                    </p>
                  </div>
                  <p className="mt-1 truncate text-sm font-semibold">
                    {topQR.label || topQR.slug}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatNumber(topQR.scans)} scan{topQR.scans === 1 ? '' : 's'}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function NeedsAttentionCard({
  isLoading,
  data,
  onSendToLapsed,
}: {
  isLoading: boolean
  data?: MarketingOverviewResponse
  onSendToLapsed: () => void
}) {
  const items: { label: string; cta: string; onClick?: () => void; to?: string; icon: React.ReactNode }[] = []
  if (data) {
    if (data.needs_attention.loyalty_disabled) {
      items.push({
        label: 'Loyalty program is disabled',
        cta: 'Enable in 2 min →',
        to: '/admin/marketing/loyalty',
        icon: <Award className="h-3.5 w-3.5" />,
      })
    }
    if (data.needs_attention.promos_expiring_soon.length > 0) {
      const first = data.needs_attention.promos_expiring_soon[0]
      const more = data.needs_attention.promos_expiring_soon.length - 1
      items.push({
        label: `Promo ${first.code} expires ${formatDateHuman(first.expires_at)}${
          more > 0 ? ` (+${more} more)` : ''
        }`,
        cta: 'Review →',
        to: '/admin/marketing/promo-codes',
        icon: <CalendarClock className="h-3.5 w-3.5" />,
      })
    }
    if (data.needs_attention.lapsed_customers > 0) {
      items.push({
        label: `${formatNumber(data.needs_attention.lapsed_customers)} lapsed customers`,
        cta: 'Send them a promo →',
        onClick: onSendToLapsed,
        icon: <UserPlus className="h-3.5 w-3.5" />,
      })
    }
    if (data.needs_attention.stale_qrs.length > 0) {
      const first = data.needs_attention.stale_qrs[0]
      const more = data.needs_attention.stale_qrs.length - 1
      items.push({
        label: `QR "${first.label || first.slug}" has no scans${
          more > 0 ? ` (+${more} more)` : ''
        }`,
        cta: 'Reprint poster →',
        to: '/admin/marketing/posters',
        icon: <QrCode className="h-3.5 w-3.5" />,
      })
    }
  }
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          Needs attention
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <>
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            All clear — nothing needs your attention right now.
          </p>
        ) : (
          items.map((it, i) => {
            const content = (
              <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 p-3 hover:bg-muted/40 transition-colors text-left">
                <div className="flex items-start gap-2 min-w-0">
                  <span className="text-muted-foreground mt-0.5">{it.icon}</span>
                  <p className="text-sm truncate">{it.label}</p>
                </div>
                <span className="text-sm font-medium text-primary whitespace-nowrap">
                  {it.cta}
                </span>
              </div>
            )
            if (it.to) {
              return (
                <Link key={i} to={it.to}>
                  {content}
                </Link>
              )
            }
            return (
              <button key={i} type="button" onClick={it.onClick} className="w-full">
                {content}
              </button>
            )
          })
        )}
      </CardContent>
    </Card>
  )
}

function QuickActionsCard() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" />
          Quick actions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <Link to="/admin/marketing/promo-codes">
          <Button variant="outline" className="w-full justify-start gap-2">
            <Tag className="h-4 w-4" />
            New promo code
          </Button>
        </Link>
        <Link to="/admin/marketing/qr-codes">
          <Button variant="outline" className="w-full justify-start gap-2">
            <QrCode className="h-4 w-4" />
            Make a QR code
          </Button>
        </Link>
        <Link to="/admin/marketing/posters">
          <Button variant="outline" className="w-full justify-start gap-2">
            <Printer className="h-4 w-4" />
            Print loyalty poster
          </Button>
        </Link>
        <Button
          variant="outline"
          className="w-full justify-start gap-2 opacity-60 cursor-not-allowed"
          disabled
          title="Coming soon"
        >
          <Send className="h-4 w-4" />
          Send a campaign
          <Badge variant="outline" className="ml-auto text-sm">
            Soon
          </Badge>
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * Guest recovery — the proof half of the feedback loop.
 *
 * Deliberately shows FOUR numbers, not one. A recovered-revenue figure quoted
 * without the discount it cost is marketing arithmetic, and the headline is
 * "revenue from recovered guests" rather than "revenue recovered" because some
 * of these guests would have come back anyway. We can defend an attribution;
 * we cannot defend a causal claim.
 */
function GuestRecoveryCard({ fromISO, toISO }: { fromISO: string; toISO: string }) {
  const { formatCurrency } = useCurrency()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['marketing', 'recovery-summary', fromISO, toISO],
    queryFn: () => apiClient.getRecoverySummary(fromISO, toISO),
    staleTime: 60_000,
  })
  const s = data?.data

  // A failed request must not render as four zeros — that reads as "the loop
  // did nothing", which is a different and much worse claim than "we could
  // not load this".
  if (isError) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-destructive">
          Couldn’t load guest recovery figures.
        </CardContent>
      </Card>
    )
  }

  // Hide only when there is genuinely nothing to say. Note redeemed is part of
  // the test: a coupon issued last month and redeemed inside this window is
  // exactly the recovered revenue this card exists to show, and keying the
  // check on issued-count alone would hide the card in that case.
  if (
    !isLoading &&
    s &&
    s.coupons_issued === 0 &&
    s.coupons_redeemed === 0 &&
    s.unresolved_low_ratings === 0
  ) {
    return null
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <LifeBuoy className="h-4 w-4 text-muted-foreground" aria-hidden />
          Guest recovery
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <div className="text-xs text-muted-foreground">Coupons sent</div>
                <div className="text-xl font-semibold">{s?.coupons_issued ?? 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Guests who came back</div>
                <div className="text-xl font-semibold">{s?.coupons_redeemed ?? 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">
                  Revenue from those visits
                </div>
                <div className="text-xl font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(s?.revenue_from_recovered ?? 0)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Discount it cost</div>
                <div className="text-xl font-semibold">
                  {formatCurrency(s?.discount_given ?? 0)}
                </div>
              </div>
            </div>
            {(s?.unresolved_low_ratings ?? 0) > 0 && (
              <Link
                to="/admin/marketing/recovery"
                className="mt-3 flex items-center gap-1 text-sm text-rose-600 hover:underline dark:text-rose-400"
              >
                {s?.unresolved_low_ratings} unhappy guest
                {s?.unresolved_low_ratings === 1 ? '' : 's'} still waiting for a reply
                <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function OccasionsCard({
  isLoading,
  occasions,
}: {
  isLoading: boolean
  occasions: MarketingOverviewResponse['occasions_this_week']
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Cake className="h-4 w-4 text-pink-500" />
          Birthdays this week
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </>
        ) : occasions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No upcoming birthdays — collect customer birthdays at checkout to send treats.
          </p>
        ) : (
          occasions.slice(0, 5).map(o => {
            const wa =
              o.phone_normalized && o.phone_normalized.length > 0
                ? `https://wa.me/${o.phone_normalized.replace(/[^0-9]/g, '')}`
                : null
            return (
              <div
                key={o.customer_id}
                className="flex items-center justify-between gap-3 rounded-md border bg-muted/20 p-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{o.name || 'A guest'}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDateHuman(o.date)}
                    {o.phone_normalized ? ` · ${o.phone_normalized}` : ''}
                  </p>
                </div>
                {wa ? (
                  <a
                    href={wa}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-primary whitespace-nowrap hover:underline"
                  >
                    Send a treat →
                  </a>
                ) : (
                  <span className="text-sm text-muted-foreground">No phone</span>
                )}
              </div>
            )
          })
        )}
      </CardContent>
    </Card>
  )
}

function LiveActivityCard({
  isLoading,
  events,
}: {
  isLoading: boolean
  events: MarketingActivityEvent[]
}) {
  return (
    <Card className="lg:col-span-2">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity className="h-4 w-4 text-emerald-500" />
          Live activity
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No marketing activity yet — make some noise →
          </p>
        ) : (
          events.map((e, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-muted/30"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={cn('shrink-0', activityTone(e.kind))}>{activityIcon(e.kind)}</span>
                <p className="truncate text-sm">{e.label}</p>
              </div>
              <p className="text-sm text-muted-foreground whitespace-nowrap">{timeAgo(e.at)}</p>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  )
}

function ChannelStatusStrip({ data }: { data: MarketingOverviewResponse }) {
  const items = [
    {
      key: 'loyalty',
      on: data.channel_status.loyalty_enabled,
      label: data.channel_status.loyalty_enabled
        ? `Loyalty program (${data.channel_status.loyalty_threshold}-visit threshold)`
        : 'Loyalty program disabled',
      to: '/admin/marketing/loyalty',
    },
    {
      key: 'promos',
      on: data.channel_status.active_promos > 0,
      label:
        data.channel_status.active_promos > 0
          ? `${formatNumber(data.channel_status.active_promos)} active promo${
              data.channel_status.active_promos === 1 ? '' : 's'
            }`
          : 'No active promos',
      to: '/admin/marketing/promo-codes',
    },
    {
      key: 'qr',
      on: data.channel_status.qr_codes > 0,
      label:
        data.channel_status.qr_codes > 0
          ? `${formatNumber(data.channel_status.qr_codes)} QR code${
              data.channel_status.qr_codes === 1 ? '' : 's'
            }`
          : 'No QR codes yet',
      to: '/admin/marketing/qr-codes',
    },
    {
      key: 'campaigns',
      on: data.channel_status.campaigns_sent > 0,
      label:
        data.channel_status.campaigns_sent > 0
          ? `${formatNumber(data.channel_status.campaigns_sent)} campaigns sent`
          : '0 campaigns sent',
      to: '/admin/marketing/campaigns',
    },
  ]
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 p-3">
        {items.map(it => (
          <Link
            key={it.key}
            to={it.to}
            className={cn(
              'flex items-center gap-2 text-sm hover:text-foreground transition-colors',
              it.on ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {it.on ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <Circle className="h-3.5 w-3.5 text-muted-foreground/60" />
            )}
            <span>{it.label}</span>
            {!it.on && <span className="font-medium text-primary">Set up →</span>}
          </Link>
        ))}
      </CardContent>
    </Card>
  )
}
